/**
 * /member/join — member self sign-up (TRUS-778, Phase 2).
 *
 * Five screens, one column, phone-first:
 *   1. What do you need help with?   (multi-select; ?need= preselects)
 *   2. Where are you?                (ZIP + a few need-specific questions)
 *   3. How can we reach you?         (first name, mobile, preferences, 18+)
 *   4. Enter the code we texted you  (6 digits + privacy consent)
 *   5. You're all set                (what happens next + places near them)
 *
 * No password and no social security number. The code in step 4 creates the
 * account and signs them in (stored exactly like a member login). A `?qr=`
 * token from a flyer QR code is passed through as `qr_token` so the request
 * is attributed to the organization that put the flyer up.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, MapPin, Phone } from 'lucide-react';
import {
  NEEDS,
  useMemberHelpLine,
  MemberJoinError,
  getJoinPlaces,
  isNeedCode,
  isValidUsPhone,
  isValidZip,
  joinResend,
  joinStart,
  joinVerify,
  normalizeUsPhone,
  type BestTime,
  type ContactMethod,
  type NearbyPlace,
  type NeedCode,
} from '@/lib/memberJoin';
import {
  CrisisLine,
  DOOR_INPUT,
  DOOR_PRIMARY,
  HelpLineFooter,
  LeaveSiteButton,
  NEED_ICONS,
} from '@/components/member-portal/MemberDoorChrome';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';
import { useAppTranslation } from '@/contexts/TranslationContext';

type Step = 1 | 2 | 3 | 4 | 5;
type YesNo = 'yes' | 'no' | '';
type BillType = 'electric_gas' | 'rent' | 'phone' | 'other' | '';

const MESSAGE_LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'zh', name: '中文' },
  { code: 'tl', name: 'Tagalog' },
];

function Choice({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`flex items-center justify-between gap-3 min-h-[52px] w-full px-4 rounded-xl border text-left font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent ${
        selected ? 'border-v2accent bg-v2accent-soft text-v2accent-ink' : 'border-v2border bg-surface text-ink hover:border-v2accent'
      }`}
    >
      <span>{children}</span>
      {selected && <Check className="w-5 h-5 flex-none" aria-hidden="true" />}
    </button>
  );
}

function YesNoQuestion({
  id,
  question,
  value,
  onChange,
  yesLabel,
  noLabel,
}: {
  id: string;
  question: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
  yesLabel: string;
  noLabel: string;
}) {
  return (
    <fieldset className="flex flex-col gap-2" aria-labelledby={`${id}-legend`}>
      <legend id={`${id}-legend`} className="font-semibold text-ink mb-1">
        {question}
      </legend>
      <Choice selected={value === 'yes'} onClick={() => onChange(value === 'yes' ? '' : 'yes')}>
        {yesLabel}
      </Choice>
      <Choice selected={value === 'no'} onClick={() => onChange(value === 'no' ? '' : 'no')}>
        {noLabel}
      </Choice>
    </fieldset>
  );
}

export default function MemberJoinPage() {
  const { t, language } = useAppTranslation();
  const helpLine = useMemberHelpLine();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qrToken = params.get('qr') || undefined;
  // TRUS-778: links from Wren in ChatGPT / Claude (and other partners) say where
  // the person came from, and may prefill the ZIP and message language.
  const source = (() => {
    const s = (params.get('src') || '').toLowerCase();
    return /^[a-z0-9_-]{1,32}$/.test(s) ? s : '';
  })();

  const [step, setStep] = useState<Step>(1);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Step 1
  const [needs, setNeeds] = useState<NeedCode[]>(() => {
    const preset = params.get('need');
    return isNeedCode(preset) ? [preset] : [];
  });
  // Step 2
  const [zip, setZip] = useState(() => {
    const preset = (params.get('zip') || '').trim();
    return /^\d{5}$/.test(preset) ? preset : '';
  });
  const [foodToday, setFoodToday] = useState<YesNo>('');
  const [safeTonight, setSafeTonight] = useState<YesNo>('');
  const [billType, setBillType] = useState<BillType>('');
  const [hasInsurance, setHasInsurance] = useState<YesNo>('');
  // Step 3
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactMethod, setContactMethod] = useState<ContactMethod>('text');
  const [bestTime, setBestTime] = useState<BestTime>('any');
  const [messageLanguage, setMessageLanguage] = useState(() => {
    const code = (params.get('lang') || language || 'en').split('-')[0].toLowerCase();
    return MESSAGE_LANGUAGES.some((l) => l.code === code) ? code : 'en';
  });
  const [notSafeToText, setNotSafeToText] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  // Step 4
  const [joinId, setJoinId] = useState('');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [resent, setResent] = useState(false);
  // Step 5
  const [places, setPlaces] = useState<NearbyPlace[] | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    headingRef.current?.focus();
    window.scrollTo(0, 0);
  }, [step]);

  const urgent = needs.includes('safety') || foodToday === 'no' || safeTonight === 'no';

  const answers = useMemo(() => {
    const a: Record<string, string> = {};
    if (needs.includes('food') && foodToday) a.food_today = foodToday;
    if (needs.includes('housing') && safeTonight) a.safe_place_tonight = safeTonight;
    if (needs.includes('bills') && billType) a.bill_type = billType;
    if (needs.includes('health') && hasInsurance) a.has_health_insurance = hasInsurance;
    if (source) a.source = source;
    return a;
  }, [needs, foodToday, safeTonight, billType, hasInsurance, source]);

  const friendlyError = (err: unknown): string => {
    if (err instanceof MemberJoinError) {
      if (err.status === 429) {
        return t('memberJoin.err_too_many', 'Too many tries. Please wait a minute and try again.');
      }
      if (err.message) return err.message;
    }
    if (!helpLine) {
      return t('memberJoin.err_unavailable_no_phone', 'We could not finish this just now. Please try again in a few minutes.');
    }
    return t(
      'memberJoin.err_unavailable',
      'We could not finish this just now. Please try again, or text or call {{phone}} and a navigator will sign you up.',
      { phone: helpLine.display },
    );
  };

  const go = (next: Step) => {
    setError('');
    setFieldError('');
    setStep(next);
  };

  const toggleNeed = (codeValue: NeedCode) => {
    setFieldError('');
    setNeeds((prev) => (prev.includes(codeValue) ? prev.filter((n) => n !== codeValue) : [...prev, codeValue]));
  };

  // ----- step transitions ---------------------------------------------------

  const continueFromNeeds = () => {
    if (needs.length === 0) {
      setFieldError(t('memberJoin.pick_at_least_one', 'Choose at least one, or "I\'m not sure".'));
      return;
    }
    go(2);
  };

  const continueFromWhere = (skip: boolean) => {
    if (!isValidZip(zip)) {
      setFieldError(t('memberJoin.zip_invalid', 'Enter a 5-digit ZIP code.'));
      return;
    }
    if (skip) {
      setFoodToday('');
      setSafeTonight('');
      setBillType('');
      setHasInsurance('');
    }
    go(3);
  };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) {
      setFieldError(t('memberJoin.name_required', 'Enter your first name.'));
      return;
    }
    if (!isValidUsPhone(phone)) {
      setFieldError(t('memberJoin.phone_invalid', 'Enter a 10-digit US mobile number.'));
      return;
    }
    if (!isAdult) {
      setFieldError(
        t(
          'memberJoin.adult_required',
          'You need to be 18 or older to sign up here. If you are younger, text or call us and we will help.',
        ),
      );
      return;
    }
    setFieldError('');
    setBusy(true);
    try {
      const res = await joinStart({
        first_name: firstName.trim(),
        phone: `+1${normalizeUsPhone(phone)}`,
        zip: zip.trim(),
        needs,
        answers,
        language: messageLanguage,
        contact_method: notSafeToText ? 'call' : contactMethod,
        best_time: bestTime,
        safe_to_text: !notSafeToText,
        is_adult: isAdult,
        ...(qrToken ? { qr_token: qrToken } : {}),
      });
      setJoinId(res.join_id);
      setPhoneMasked(res.phone_masked || '');
      setCode('');
      setResent(false);
      go(4);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await joinResend(joinId);
      if (res.phone_masked) setPhoneMasked(res.phone_masked);
      setResent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) {
      setFieldError(t('memberJoin.code_invalid', 'Enter the 6-digit code.'));
      return;
    }
    if (!consent) {
      setFieldError(t('memberJoin.consent_required', 'Check the box to agree to the privacy notice.'));
      return;
    }
    setFieldError('');
    setBusy(true);
    try {
      await joinVerify(joinId, code);
      go(5);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  // Places near them, for the "all set" screen: up to 3 across their needs.
  useEffect(() => {
    if (step !== 5) return;
    const lookups = needs.filter((n) => n !== 'not_sure');
    if (lookups.length === 0) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    Promise.allSettled(lookups.map((n) => getJoinPlaces(n, zip.trim()))).then((results) => {
      if (cancelled) return;
      const seen = new Set<string>();
      const merged: NearbyPlace[] = [];
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        for (const p of r.value) {
          if (!p?.name || seen.has(p.name)) continue;
          seen.add(p.name);
          merged.push(p);
        }
      }
      setPlaces(merged.slice(0, 3));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ----- render helpers -----------------------------------------------------

  const needLabel = (c: NeedCode) => {
    const n = NEEDS.find((x) => x.code === c)!;
    return t(n.labelKey, n.label);
  };

  const errorBox = (error || fieldError) && (
    <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-danger-ink">
      {fieldError || error}
      {error && !fieldError && helpLine && (
        <span className="block mt-2">
          <a href={helpLine.sms} className="font-semibold underline">
            {t('memberJoin.text_us', 'Text us')}
          </a>{' '}
          ·{' '}
          <a href={helpLine.tel} className="font-semibold underline">
            {t('memberJoin.call_us', 'Call us')}
          </a>
        </span>
      )}
    </div>
  );

  const headingCls = 'font-display text-[26px] leading-tight font-semibold tracking-[-0.02em] text-ink outline-none';

  // ----- screens ------------------------------------------------------------

  let body: React.ReactNode;
  if (step === 1) {
    body = (
      <>
        <div>
          <h1 ref={headingRef} tabIndex={-1} className={headingCls}>
            {t('memberJoin.s1_title', 'What do you need help with?')}
          </h1>
          <p className="mt-1 text-ink-700">{t('memberJoin.s1_sub', 'Choose all that apply.')}</p>
        </div>
        <div className="flex flex-col gap-2.5">
          {NEEDS.map((n) => {
            const Icon = NEED_ICONS[n.code];
            return (
              <Choice key={n.code} selected={needs.includes(n.code)} onClick={() => toggleNeed(n.code)}>
                <span className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-v2accent" aria-hidden="true" />
                  {t(n.labelKey, n.label)}
                </span>
              </Choice>
            );
          })}
        </div>
        {needs.includes('safety') && <CrisisLine prominent />}
        {errorBox}
        <button type="button" onClick={continueFromNeeds} className={`${DOOR_PRIMARY} w-full`}>
          {t('memberJoin.continue', 'Continue')}
        </button>
      </>
    );
  } else if (step === 2) {
    const hasQuestions = needs.some((n) => n === 'food' || n === 'housing' || n === 'bills' || n === 'health');
    body = (
      <>
        <h1 ref={headingRef} tabIndex={-1} className={headingCls}>
          {t('memberJoin.s2_title', 'Where are you?')}
        </h1>
        <div>
          <label htmlFor="join-zip" className="block font-semibold text-ink mb-1">
            {t('memberJoin.zip', 'ZIP code')}
          </label>
          <input
            id="join-zip"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            value={zip}
            onChange={(e) => {
              setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
              setFieldError('');
            }}
            className={`${DOOR_INPUT} max-w-[10rem]`}
          />
          <p className="mt-1 text-sm text-ink-500">{t('memberJoin.zip_hint', 'So we show places close to you.')}</p>
        </div>

        {needs.includes('food') && (
          <YesNoQuestion
            id="q-food"
            question={t('memberJoin.q_food_today', 'Do you have food for today?')}
            value={foodToday}
            onChange={setFoodToday}
            yesLabel={t('memberJoin.yes', 'Yes')}
            noLabel={t('memberJoin.q_food_no', 'No, I need food today')}
          />
        )}
        {needs.includes('housing') && (
          <YesNoQuestion
            id="q-housing"
            question={t('memberJoin.q_safe_tonight', 'Do you have a safe place to sleep tonight?')}
            value={safeTonight}
            onChange={setSafeTonight}
            yesLabel={t('memberJoin.yes', 'Yes')}
            noLabel={t('memberJoin.q_safe_no', 'No, I need a place tonight')}
          />
        )}
        {needs.includes('bills') && (
          <fieldset className="flex flex-col gap-2" aria-labelledby="q-bill-legend">
            <legend id="q-bill-legend" className="font-semibold text-ink mb-1">
              {t('memberJoin.q_bill', 'Which bill is hardest right now?')}
            </legend>
            {(
              [
                ['electric_gas', t('memberJoin.bill_electric_gas', 'Electric or gas')],
                ['rent', t('memberJoin.bill_rent', 'Rent')],
                ['phone', t('memberJoin.bill_phone', 'Phone')],
                ['other', t('memberJoin.bill_other', 'Something else')],
              ] as [BillType, string][]
            ).map(([value, label]) => (
              <Choice key={value} selected={billType === value} onClick={() => setBillType(billType === value ? '' : value)}>
                {label}
              </Choice>
            ))}
          </fieldset>
        )}
        {needs.includes('health') && (
          <YesNoQuestion
            id="q-health"
            question={t('memberJoin.q_insurance', 'Do you have health insurance, like Medi-Cal?')}
            value={hasInsurance}
            onChange={setHasInsurance}
            yesLabel={t('memberJoin.yes', 'Yes')}
            noLabel={t('memberJoin.q_insurance_no', "No, or I'm not sure")}
          />
        )}

        {urgent && <CrisisLine prominent />}
        {errorBox}
        {hasQuestions && (
          <button
            type="button"
            onClick={() => continueFromWhere(true)}
            className="self-start min-h-[44px] text-v2accent-ink font-semibold underline"
          >
            {t('memberJoin.skip_questions', 'Skip these questions')}
          </button>
        )}
        <button type="button" onClick={() => continueFromWhere(false)} className={`${DOOR_PRIMARY} w-full`}>
          {t('memberJoin.continue', 'Continue')}
        </button>
      </>
    );
  } else if (step === 3) {
    body = (
      <form onSubmit={sendCode} className="flex flex-col gap-5" noValidate>
        <h1 ref={headingRef} tabIndex={-1} className={headingCls}>
          {t('memberJoin.s3_title', 'How can we reach you?')}
        </h1>
        <div>
          <label htmlFor="join-first-name" className="block font-semibold text-ink mb-1">
            {t('memberJoin.first_name', 'First name')}
          </label>
          <input
            id="join-first-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={DOOR_INPUT}
          />
        </div>
        <div>
          <label htmlFor="join-phone" className="block font-semibold text-ink mb-1">
            {t('memberJoin.mobile', 'Mobile number')}
          </label>
          <input
            id="join-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={DOOR_INPUT}
          />
          <p className="mt-1 text-sm text-ink-500">
            {t('memberJoin.mobile_hint', 'We text you a code now, and your navigator texts you here.')}
          </p>
        </div>

        <fieldset className="flex flex-col gap-2" aria-labelledby="join-method-legend" disabled={notSafeToText}>
          <legend id="join-method-legend" className="font-semibold text-ink mb-1">
            {t('memberJoin.best_way', 'Best way to reach you')}
          </legend>
          <div className="grid grid-cols-2 gap-2.5">
            <Choice selected={!notSafeToText && contactMethod === 'text'} onClick={() => setContactMethod('text')}>
              {t('memberJoin.method_text', 'Text')}
            </Choice>
            <Choice selected={notSafeToText || contactMethod === 'call'} onClick={() => setContactMethod('call')}>
              {t('memberJoin.method_call', 'Call')}
            </Choice>
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="join-best-time" className="block font-semibold text-ink mb-1">
              {t('memberJoin.best_time', 'Best time')}
            </label>
            <select
              id="join-best-time"
              value={bestTime}
              onChange={(e) => setBestTime(e.target.value as BestTime)}
              className={DOOR_INPUT}
            >
              <option value="any">{t('memberJoin.time_any', 'Any time')}</option>
              <option value="morning">{t('memberJoin.time_morning', 'Morning')}</option>
              <option value="afternoon">{t('memberJoin.time_afternoon', 'Afternoon')}</option>
              <option value="evening">{t('memberJoin.time_evening', 'Evening')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="join-language" className="block font-semibold text-ink mb-1">
              {t('memberJoin.language', 'Language for messages')}
            </label>
            <select
              id="join-language"
              value={messageLanguage}
              onChange={(e) => setMessageLanguage(e.target.value)}
              className={DOOR_INPUT}
            >
              {MESSAGE_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            checked={notSafeToText}
            onChange={(e) => setNotSafeToText(e.target.checked)}
            className="mt-0.5 w-6 h-6 flex-none accent-v2accent"
          />
          <span className="text-ink">
            {t('memberJoin.not_safe_to_text', 'It is not safe to text me. Call only, and do not leave a message.')}
            {notSafeToText && (
              <span className="block text-sm text-ink-500 mt-1">
                {t(
                  'memberJoin.not_safe_code_note',
                  'We still text you one code now, to confirm the number. After that we only call.',
                )}
              </span>
            )}
          </span>
        </label>
        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            checked={isAdult}
            onChange={(e) => {
              setIsAdult(e.target.checked);
              setFieldError('');
            }}
            required
            className="mt-0.5 w-6 h-6 flex-none accent-v2accent"
          />
          <span className="text-ink font-semibold">{t('memberJoin.is_adult', 'I am 18 or older')}</span>
        </label>

        {errorBox}
        <button type="submit" disabled={busy} className={`${DOOR_PRIMARY} w-full`}>
          {busy ? t('memberJoin.sending', 'Sending…') : t('memberJoin.send_code', 'Send my code')}
        </button>
      </form>
    );
  } else if (step === 4) {
    body = (
      <form onSubmit={finish} className="flex flex-col gap-5" noValidate>
        <div>
          <h1 ref={headingRef} tabIndex={-1} className={headingCls}>
            {t('memberJoin.s4_title', 'Enter the code we texted you')}
          </h1>
          <p className="mt-1 text-ink-700">
            {t('memberJoin.sent_to', 'Sent to {{phone}}.', { phone: phoneMasked || phone })}{' '}
            <button
              type="button"
              onClick={() => go(3)}
              className="min-h-[44px] font-semibold text-v2accent-ink underline"
            >
              {t('memberJoin.change_number', 'Change number')}
            </button>
          </p>
        </div>
        <div>
          <label htmlFor="join-code" className="block font-semibold text-ink mb-1">
            {t('memberJoin.six_digit_code', '6-digit code')}
          </label>
          <input
            id="join-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setFieldError('');
            }}
            className={`${DOOR_INPUT} max-w-[12rem] text-center text-2xl tracking-[0.4em] font-mono`}
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={resendCode}
              disabled={busy}
              className="min-h-[44px] font-semibold text-v2accent-ink underline disabled:opacity-50"
            >
              {t('memberJoin.send_new_code', 'Send a new code')}
            </button>
            {resent && <span className="text-sm text-ink-500">{t('memberJoin.code_resent', 'New code sent.')}</span>}
          </div>
        </div>

        <section className="rounded-xl border border-v2border bg-paper p-4 flex flex-col gap-2">
          <h2 className="font-semibold text-ink">{t('memberJoin.your_privacy', 'Your privacy')}</h2>
          <ul className="list-disc pl-5 text-ink-700 space-y-1">
            <li>{t('memberJoin.privacy_1', 'We share your answers only with places that can help you.')}</li>
            <li>{t('memberJoin.privacy_2', 'We never sell your information.')}</li>
            <li>{t('memberJoin.privacy_3', 'You can stop at any time by texting STOP.')}</li>
          </ul>
          <Link
            to="/consent/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center min-h-[44px] font-semibold text-v2accent-ink underline"
          >
            {t('memberJoin.read_privacy', 'Read the full privacy notice')}
          </Link>
        </section>

        <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setFieldError('');
            }}
            required
            className="mt-0.5 w-6 h-6 flex-none accent-v2accent"
          />
          <span className="text-ink">
            {t(
              'memberJoin.consent',
              'I agree to the privacy notice and to messages from WeaveIn about my request.',
            )}
          </span>
        </label>

        {errorBox}
        <button type="submit" disabled={busy} className={`${DOOR_PRIMARY} w-full`}>
          {busy ? t('memberJoin.checking', 'Checking…') : t('memberJoin.finish', 'Finish')}
        </button>
      </form>
    );
  } else {
    const needList = needs.filter((n) => n !== 'not_sure').map(needLabel).join(', ').toLowerCase();
    body = (
      <>
        <div>
          <h1 ref={headingRef} tabIndex={-1} className={headingCls}>
            {t('memberJoin.s5_title', "You're all set, {{name}}.", { name: firstName.trim() })}
          </h1>
          <p className="mt-2 text-ink-700">
            {t(
              'memberJoin.s5_sub',
              'Your account is ready. No password needed: next time, sign in with a code we text you.',
            )}
          </p>
        </div>

        {urgent && <CrisisLine prominent />}

        {places && places.length > 0 && (
          <section className="flex flex-col gap-2.5">
            <h2 className="font-display text-lg font-semibold text-ink">
              {t('memberJoin.places_near', 'Places near {{zip}}', { zip })}
            </h2>
            {places.map((p) => (
              <div key={p.name} className="rounded-xl border border-v2border bg-surface p-4 flex flex-col gap-1.5">
                <p className="font-semibold text-ink">{p.name}</p>
                <p className="text-sm text-ink-700">
                  {[
                    p.hours,
                    typeof p.distance_miles === 'number'
                      ? t('memberJoin.miles', '{{n}} miles', { n: p.distance_miles.toFixed(1) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {p.address && <p className="text-sm text-ink-700">{p.address}</p>}
                <div className="flex flex-wrap gap-2 pt-1">
                  {p.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.name} ${p.address}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-v2border text-ink font-semibold hover:border-v2accent"
                    >
                      <MapPin className="w-4 h-4" aria-hidden="true" />
                      {t('memberJoin.directions', 'Directions')}
                    </a>
                  )}
                  {p.phone && (
                    <a
                      href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}
                      className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-v2border text-ink font-semibold hover:border-v2accent"
                    >
                      <Phone className="w-4 h-4" aria-hidden="true" />
                      {t('memberJoin.call_place', 'Call')}
                    </a>
                  )}
                  {p.website && (
                    <a
                      href={p.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center min-h-[44px] px-3 rounded-lg border border-v2border text-ink font-semibold hover:border-v2accent"
                    >
                      {t('memberJoin.website', 'Website')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </section>
        )}

        <section className="rounded-xl border border-v2border bg-paper p-4 flex flex-col gap-2">
          <h2 className="font-display text-lg font-semibold text-ink">
            {t('memberJoin.what_next', 'What happens next')}
          </h2>
          <ol className="list-decimal pl-5 text-ink-700 space-y-1.5">
            <li>{t('memberJoin.next_1', 'Within 1 hour we text you to check a few details.')}</li>
            <li>
              {needList
                ? t(
                    'memberJoin.next_2_needs',
                    'We ask up to four local organizations near {{zip}} to take your request for {{needs}}.',
                    { zip, needs: needList },
                  )
                : t('memberJoin.next_2', 'We ask up to four local organizations near {{zip}} to take your request.', {
                    zip,
                  })}
            </li>
            <li>
              {t(
                'memberJoin.next_3',
                'When one says yes, we text you their name, when they will contact you, and what to bring.',
              )}
            </li>
            <li>{t('memberJoin.next_4', 'In 3 days we check that you got help. If not, we ask another organization.')}</li>
          </ol>
        </section>

        <button type="button" onClick={() => navigate('/member/home')} className={`${DOOR_PRIMARY} w-full`}>
          {t('memberJoin.go_to_my_page', 'Go to my page')}
        </button>
      </>
    );
  }

  return (
    <div className={`${AUTH_BACKDROP} flex flex-col`}>
      <header className="bg-surface border-b border-v2border">
        <div className="max-w-xl w-full mx-auto px-4 py-2 flex items-center justify-between gap-3">
          {step > 1 && step < 5 ? (
            <button
              type="button"
              onClick={() => go((step - 1) as Step)}
              className="inline-flex items-center gap-1.5 min-h-[44px] min-w-[44px] -ml-2 px-2 rounded-md text-ink font-semibold hover:bg-paper-2"
            >
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              {t('memberJoin.back', 'Back')}
            </button>
          ) : step === 1 ? (
            <Link
              to="/member/home"
              className="inline-flex items-center gap-1.5 min-h-[44px] -ml-2 px-2 rounded-md text-ink font-semibold hover:bg-paper-2"
            >
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              {t('memberJoin.back', 'Back')}
            </Link>
          ) : (
            <span />
          )}
          {step < 5 && (
            <span className="text-sm font-medium text-ink-700" aria-live="polite">
              {t('memberJoin.step_n_of_4', 'Step {{n}} of 4', { n: step })}
            </span>
          )}
          <LeaveSiteButton compact />
        </div>
        {step < 5 && (
          <div
            className="h-1.5 bg-paper-2"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={4}
            aria-valuenow={step}
            aria-label={t('memberJoin.progress', 'Sign-up progress')}
          >
            <div className="h-full bg-v2accent transition-all" style={{ width: `${(step / 4) * 100}%` }} />
          </div>
        )}
      </header>

      <main className="flex-1 bg-surface">
        <div className="max-w-xl w-full mx-auto px-4 py-6 flex flex-col gap-5">
          {body}
          <HelpLineFooter />
          {step === 1 && (
            <p className="text-center text-sm text-ink-500">
              {t('memberJoin.have_account', 'Already signed up?')}{' '}
              <Link to="/member/login" className="font-semibold text-v2accent-ink underline">
                {t('memberJoin.sign_in', 'Sign in')}
              </Link>
            </p>
          )}
          {!urgent && step !== 5 && (
            <div className="pt-2 border-t border-v2border">
              <CrisisLine />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
